import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import { Heart, Headphones, Play, Pause } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const favoritesTexts = {
  de: {
    title: 'Meine Favoriten',
    savedStations: 'gespeicherte Sender',
    noFavoritesTitle: 'Noch keine Favoriten',
    noFavoritesText:
      'Entdecke Sender und speichere deine Lieblingsstationen',
    discoverStations: 'Sender entdecken',
    plays: 'Wiedergaben',
  },

  en: {
    title: 'My Favorites',
    savedStations: 'saved stations',
    noFavoritesTitle: 'No favorites yet',
    noFavoritesText:
      'Start exploring and save your favorite stations',
    discoverStations: 'Discover Stations',
    plays: 'plays',
  },

  no: {
    title: 'Mine favoritter',
    savedStations: 'lagrede stasjoner',
    noFavoritesTitle: 'Ingen favoritter ennå',
    noFavoritesText:
      'Utforsk stasjoner og lagre favorittene dine',
    discoverStations: 'Oppdag stasjoner',
    plays: 'avspillinger',
  },

  ko: {
    title: '내 즐겨찾기',
    savedStations: '저장된 방송국',
    noFavoritesTitle: '아직 즐겨찾기가 없습니다',
    noFavoritesText:
      '방송국을 탐색하고 좋아하는 방송국을 저장하세요',
    discoverStations: '방송국 탐색',
    plays: '재생',
  },

  fi: {
    title: 'Omat suosikit',
    savedStations: 'tallennettua asemaa',
    noFavoritesTitle: 'Ei suosikkeja vielä',
    noFavoritesText:
      'Tutustu asemiin ja tallenna suosikkisi',
    discoverStations: 'Löydä asemia',
    plays: 'toistoa',
  },
};

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { playStation, currentStation, isPlaying } = useAudioPlayer();
  const { language } = useLanguage();

  const text = favoritesTexts[language] || favoritesTexts.en;

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const res = await api.getFavorites();
      setFavorites(res.data);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (stationId) => {
    try {
      await api.removeFavorite(stationId);
      setFavorites(favorites.filter((s) => s.station_id !== stationId));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const getSavedStationsText = () => {
    if (language === 'ko') {
      return `${favorites.length} ${text.savedStations}`;
    }

    return `${favorites.length} ${text.savedStations}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-40 md:pb-28">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Heart size={40} weight="duotone" className="text-cyan-400" />

            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-black text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.title}
            </h1>
          </div>

          <p className="text-base text-gray-300">{getSavedStationsText()}</p>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-24">
            <Heart
              size={64}
              weight="duotone"
              className="text-gray-600 mx-auto mb-6"
            />

            <h3 className="text-xl font-semibold text-white mb-3">
              {text.noFavoritesTitle}
            </h3>

            <p className="text-gray-400 mb-6">{text.noFavoritesText}</p>

            <button
              onClick={() => navigate('/discover')}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full px-8 py-3 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all"
            >
              {text.discoverStations}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {favorites.map((station) => {
              const isCurrentlyPlaying =
                currentStation?.station_id === station.station_id && isPlaying;

              return (
                <motion.div
                  key={station.station_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`favorite-card-${station.station_id}`}
                  className="bg-[#0A0E17] border border-white/5 rounded-2xl p-6 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/station/${station.station_id}`)}
                >
                  <div className="aspect-square rounded-xl overflow-hidden mb-4 relative">
                    <img
                      src={
                        station.logo_url ||
                        'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400'
                      }
                      alt={station.name}
                      onError={(e) => {
                        e.target.src =
                          'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400';
                      }}
                      className="w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <button
                        data-testid={`fav-play-btn-${station.station_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          playStation(station);
                        }}
                        className="w-14 h-14 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all"
                      >
                        {isCurrentlyPlaying ? (
                          <Pause size={24} weight="fill" />
                        ) : (
                          <Play size={24} weight="fill" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors line-clamp-1">
                      {station.name}
                    </h3>

                    <button
                      data-testid={`remove-favorite-${station.station_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(station.station_id);
                      }}
                      className="text-cyan-400 hover:text-red-400 transition-colors"
                    >
                      <Heart size={20} weight="fill" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-400 mb-2">
                    {station.country} • {station.genre}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Headphones size={14} />
                    <span>
                      {station.play_count?.toLocaleString() || 0} {text.plays}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Favorites;